import React, { FC, useEffect, useState } from 'react';
import { Button } from 'components/atoms/button';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Popover } from 'components/atoms/popover';
import { Filter } from 'components/molecules/filters/types';
import { TaskFilterButton } from 'components/molecules/views/components';
import { FilterBoxWrapper } from 'components/molecules/views/filter-popover';
import { FilterPopoverDone } from 'components/molecules/views/filter-popover/filter-popover-done';
import { useTranslation } from 'hooks/use-translation';
import { isChildNode } from 'utils/is-child-node';
import { FilterableField } from './fields';
import { FilterBox } from './filter-box';
import { FieldFilter } from './filters';

export interface Props {
  loading: boolean;
  filter: Filter;
  fields: FilterableField[];
  onChange: (filter: Filter) => void;
  reset: () => void;
  onVisibilityChange?: (visible: boolean) => void;
  hideClearFilter?: boolean;
}

export const TaskFilterPopover: FC<Props> = ({
  loading,
  filter,
  fields,
  onChange,
  onVisibilityChange,
  reset,
  hideClearFilter = false,
}) => {
  const clearCaption = useTranslation('captions.buttons.clear_filter');
  const [visible, setVisible] = useState(false);
  const isChild = isChildNode([
    'filter-box',
    'ant-dropdown',
    'ant-picker-dropdown',
    'ant-select-dropdown',
    'ant-popover-inner-content',
  ]);

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);

  return (
    <>
      <Popover
        content={
          <>
            <FilterBoxWrapper>
              <FilterBox fields={fields} filter={filter} onChange={onChange} />
            </FilterBoxWrapper>
            <FilterPopoverDone onClick={() => setVisible(false)} />
          </>
        }
        trigger={['click']}
        placement="bottom"
        autoAdjustOverflow
        arrowPointAtCenter
        visible={visible}
        destroyTooltipOnHide
      >
        <OutsideClickHandler
          allowed={isChild}
          onOutsideClick={() => setVisible(false)}
        >
          <TaskFilterButton
            data-cy="buttons.filter-button"
            onClick={() => setVisible(!visible)}
            loading={loading}
          >
            {useTranslation('captions.buttons.filter', {
              count: filter.criteria.filter(FieldFilter.isValid).length,
            })}
          </TaskFilterButton>
        </OutsideClickHandler>
      </Popover>
      {!loading &&
      !hideClearFilter &&
      filter.criteria.filter((x) => FieldFilter.isValid(x)).length > 0 ? (
        <Button size="small" type="link" onClick={reset}>
          {clearCaption}
        </Button>
      ) : null}
    </>
  );
};
